import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

function EditCoa({ sample = false }) {
    const { recordId } = useParams();
    const navigate = useNavigate();

    // Sample COAs (pre-shipment / approvals) mirror regular COAs in a parallel collection and storage folder
    const recordsCollection = sample ? 'sample_coa_records' : 'coa_records';
    const storageFolder = sample ? 'sample_coas' : 'coas';
    const dashboardPath = sample ? '/sample-coa-dashboard' : '/coa-dashboard';

    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);

    const [formData, setFormData] = useState({
        productId: '',
        customerId: '',
        batchNumber: '',
        mfgDate: '',
        dispatchDate: '',
        qtyDispatched: '', // NEW: State for quantity
        purpose: '',
        courierName: '',
        trackingNumber: '',
    });
    const [analysisResults, setAnalysisResults] = useState([{ test: '', specification: '', result: '' }]);
    const [newCoaFile, setNewCoaFile] = useState(null);
    const [originalRecord, setOriginalRecord] = useState(null);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                const coaDocRef = doc(db, recordsCollection, recordId);
                const coaDocSnap = await getDoc(coaDocRef);

                if (!coaDocSnap.exists()) {
                    alert("COA Record not found.");
                    navigate(dashboardPath);
                    return;
                }
                const coaData = coaDocSnap.data();
                setOriginalRecord({ id: coaDocSnap.id, ...coaData });
                
                setFormData({
                    productId: coaData.productId || '',
                    customerId: coaData.parameters?.customerId || '', 
                    batchNumber: coaData.batchNumber || '',
                    mfgDate: coaData.mfgDate || '',
                    dispatchDate: coaData.dispatchDate || coaData.expDate || '',
                    qtyDispatched: coaData.qtyDispatched || '', // Initialize with existing data or empty
                    purpose: coaData.purpose || 'Pre-shipment',
                    courierName: coaData.courierName || '',
                    trackingNumber: coaData.trackingNumber || '',
                });

                setAnalysisResults(coaData.analysisResults || []);

                const [productsSnapshot, customersSnapshot] = await Promise.all([
                    getDocs(collection(db, 'products')),
                    getDocs(collection(db, 'customers'))
                ]);
                setProducts(productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                setCustomers(customersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchAllData();
    }, [recordId, navigate, recordsCollection, dashboardPath]);

    const handleFormChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleFileChange = (e) => { if (e.target.files[0]) setNewCoaFile(e.target.files[0]); };
    const handleAnalysisChange = (index, e) => {
        const newResults = analysisResults.map((item, i) => i === index ? { ...item, [e.target.name]: e.target.value } : item);
        setAnalysisResults(newResults);
    };
    const addAnalysisRow = () => setAnalysisResults([...analysisResults, { test: '', specification: '', result: '' }]);
    const removeAnalysisRow = (index) => setAnalysisResults(analysisResults.filter((_, i) => i !== index));

    const handleUpdate = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);

        try {
            const selectedProduct = products.find(p => p.id === formData.productId);
            const selectedCustomer = customers.find(c => c.id === formData.customerId);

            let updatedData = {
                ...formData,
                analysisResults,
                productName: selectedProduct?.productName || originalRecord.productName, 
                customerName: selectedCustomer?.name || originalRecord.customerName, 
                customerCode: selectedCustomer?.companyCode || originalRecord.customerCode,
            };
            // purpose/courier/tracking only apply to sample COAs; keep them out of regular records
            if (!sample) {
                delete updatedData.purpose;
                delete updatedData.courierName;
                delete updatedData.trackingNumber;
            }

            if (newCoaFile) {
                const filePath = `${storageFolder}/${formData.batchNumber}/${originalRecord.coaNumber}-${newCoaFile.name}`;
                const newFileRef = ref(storage, filePath);
                await uploadBytes(newFileRef, newCoaFile);
                updatedData.fileUrl = await getDownloadURL(newFileRef);
                updatedData.fileName = newCoaFile.name;
                updatedData.filePath = filePath;
                if (originalRecord.filePath) {
                    await deleteObject(ref(storage, originalRecord.filePath));
                }
            }

            const coaDocRef = doc(db, recordsCollection, recordId);
            await updateDoc(coaDocRef, updatedData);
            alert('COA Record updated successfully!');
            navigate(dashboardPath);
        } catch (error) {
            console.error("Error updating COA record: ", error);
            alert("Failed to update record.");
        } finally {
            setSubmitLoading(false);
        }
    };

    if (loading) return <p>Loading record for editing...</p>;

    return (
        <div>
            <h2>Edit {sample ? 'Sample ' : ''}COA Record for Batch: {originalRecord?.batchNumber}</h2>
            <form onSubmit={handleUpdate}>
                <fieldset style={{ padding: '15px', border: '1px solid #ccc' }}>
                    <legend>Main Details</legend>
                    <label>Product:</label><br/>
                    <select name="productId" value={formData.productId} onChange={handleFormChange} required><option value="">-- Select a Product --</option>{products.map(p => <option key={p.id} value={p.id}>{p.productName}</option>)}</select><br/><br/>
                    <label>Customer:</label><br/>
                    <select name="customerId" value={formData.customerId} onChange={handleFormChange} required><option value="">-- Select a Customer --</option>{customers.map(c => <option key={c.id} value={c.id}>{c.companyCode || c.name}</option>)}</select><br/><br/>
                    <label>Batch Number:</label><br/>
                    <input name="batchNumber" type="text" value={formData.batchNumber} onChange={handleFormChange} required /><br/><br/>
                    <label>Manufacturing Date (YYYY-MM):</label><br/>
                    <input name="mfgDate" type="month" value={formData.mfgDate} onChange={handleFormChange} required /><br/><br/>
                    <label>Date of Dispatch:</label><br/>
                    <input name="dispatchDate" type="date" value={formData.dispatchDate} onChange={handleFormChange} required /><br/><br/>
                    
                    {sample && (<>
                        <label>Purpose:</label><br/>
                        <select name="purpose" value={formData.purpose} onChange={handleFormChange} required>
                            <option>Pre-shipment</option><option>Approval</option><option>R&D</option><option>Other</option>
                        </select><br/><br/>
                        <label>Courier / Logistics Name (Optional):</label><br/>
                        <input name="courierName" type="text" value={formData.courierName} onChange={handleFormChange} placeholder="e.g. DHL, Blue Dart" /><br/><br/>
                        <label>Tracking Number (Optional):</label><br/>
                        <input name="trackingNumber" type="text" value={formData.trackingNumber} onChange={handleFormChange} placeholder="e.g. AWB / docket number" /><br/><br/>
                    </>)}
                    {/* NEW: Input for Quantity Dispatched */}
                    <label>Quantity Dispatched:</label><br/>
                    <input name="qtyDispatched" type="text" value={formData.qtyDispatched} onChange={handleFormChange} placeholder="e.g. 1000 Kgs" /><br/><br/>
                </fieldset>
                <fieldset style={{ marginTop: '20px', padding: '15px', border: '1-px solid #ccc' }}>
                    <legend>Analysis Results</legend>
                    {analysisResults.map((row, index) => (
                        <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                            <input name="test" value={row.test} onChange={e => handleAnalysisChange(index, e)} placeholder="Test Parameter" required />
                            <input name="specification" value={row.specification} onChange={e => handleAnalysisChange(index, e)} placeholder="Specification" required />
                            <input name="result" value={row.result} onChange={e => handleAnalysisChange(index, e)} placeholder="Result" required />
                            <button type="button" onClick={() => removeAnalysisRow(index)}>Remove</button>
                        </div>
                    ))}
                    <button type="button" onClick={addAnalysisRow}>+ Add Row</button>
                </fieldset>
                <fieldset style={{ marginTop: '20px', padding: '15px', border: '1-px solid #ccc' }}>
                    <legend>COA Document</legend>
                    <p>Current File: <a href={originalRecord?.fileUrl} target="_blank" rel="noopener noreferrer">{originalRecord?.fileName}</a></p>
                    <label>Upload New File to Replace (optional):</label><br/>
                    <input type="file" onChange={handleFileChange} />
                </fieldset>
                <button type="submit" disabled={submitLoading} style={{ marginTop: '20px', padding: '10px 20px' }}>{submitLoading ? 'Updating Record...' : 'Save Changes'}</button>
                <button type="button" onClick={() => navigate(dashboardPath)} style={{ marginLeft: '10px' }}>Cancel</button>
            </form>
        </div>
    );
}

export default EditCoa;